import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
	Form,
	Button,
	Row,
	Col,
	Card,
	notification,
	Collapse,
	Tag,
	Spin,
	Divider,
	Badge,
	Table,
} from "antd";
import DefaultLayout from "../../components/layout/DefaultLayout";
import Header from "../../components/header";
import { UserOutlined } from "@ant-design/icons";
import userAccessService from "../../services/userAccess";
import approvalService from "../../services/approver";
import CommentSystem from "../../components/comment";
import CreatedByComponent from "../../components/commonDetails/createdBy";
import ApproversComponent from "../../components/commonDetails/approvers";
import ApproveDisapproveComponent from "./../../components/commonDetails/approveDisapprove/index";
import TaskStatus from "../../components/commonDetails/taskStatus";
import { replaceLineBreaks } from "../../utils/helper";
import FeedbackModal from "../../components/feedbackModal";
import DeploymentModal from "../../components/deploymentModal";
import TaskModal from "../../components/taskModal";
import AddApproversComponent from "../../components/commonDetails/manageApprovers";
import { useSelector } from "react-redux";
import { canManageApprovers } from "../../utils/permissionUtils";

const { Panel } = Collapse;

const UserAccessDetails = () => {
	const navigate = useNavigate();
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(true);
	const [actionLoading, setActionLoading] = useState(false);
	const [data, setData] = useState(null);
	const { id } = useParams();
	const [assignedUserId, setAssignedUserId] = useState(null);
	const [status, setStatus] = useState(null);
	const [formId] = useState(9);
	const [specificReturnGroupId, setSpecificReturnGroupId] = useState([]);

	const { currentUser } = useSelector((state) => state.user);
	const hasEditPermission = currentUser?.["roles.permission"]?.some(p => p.name === "Workflow-UserAccessForm-edit");
	// const hasEditPermission = canManageApprovers(currentUser, "UserAccessForm");

	// PRINT STYLES & PRINT HANDLER
	useEffect(() => {
		const style = document.createElement("style");
		style.innerHTML = `
      @media print {
        @page {
          size: A4;
          margin: 15mm;
        }
        body {
          margin: 0;
          padding: 0;
          background: #fff !important;
        }
        p {
          font-size: 12px !important;
        }
        #qqq, .ant-layout-sider, .ant-layout-header, .ant-btn, .ant-notification {
          display: none !important;
        }
        #ppp {
          display: block !important;
          width: 100% !important;
          margin: 0 !important;
          padding: 0 !important;
          border: none !important;
          background: #fff !important;
          position: static !important;
        }
        #ppp * {
          visibility: visible !important;
        }
        .ant-card {
          border: none !important;
          margin-bottom: 10px !important;
        }
        .ant-card-head {
          border-bottom: 1px solid #f0f0f0 !important;
          padding: 0 10px !important;
        }
        .ant-card-body {
          padding: 10px !important;
        }
        .ant-divider {
          margin: 10px 0 !important;
        }
        .ant-tag {
          border: 1px solid #d9d9d9 !important;
          background: #fafafa !important;
          color: rgba(0, 0, 0, 0.85) !important;
          print-color-adjust: exact;
        }
        table {
          width: 100% !important;
          border-collapse: collapse !important;
          page-break-inside: auto;
        }
        tr {
          page-break-inside: avoid;
          page-break-after: auto;
        }
        th, td {
          border: 1px solid #f0f0f0 !important;
          padding: 8px !important;
        }
        .no-print {
          display: none !important;
        }
        .page-break {
          page-break-before: always;
        }
      }
    `;
		document.head.appendChild(style);
		return () => {
			document.head.removeChild(style);
		};
	}, []);

	const getStatusColor = (status) => {
		switch (status) {
			case "Approved":
				return "green";
			case "Pending":
				return "yellow";
			default:
				return "blue";
		}
	};
	const columns = [
		{
			title: "Approver Name",
			dataIndex: "userName",
			key: "userName",
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			render: (status) => (
				<Tag style={{ fontSize: "11px" }} color={getStatusColor(status)}>
					{status}
				</Tag>
			),
		},
		{
			title: "Action Performed at",
			dataIndex: "status_at",
			key: "status_at",
		},
	];
	const componentss = {
		header: {
			cell: (props) => <th {...props} style={{ fontSize: "10px" }} />,
		},
	};
	const tableData = data?.approvers?.flatMap((approver) =>
		approver.users
			.filter(
				(user) =>
					user.status_at && // only users with a datetime in status_at
					!(
						user.is_parallel === false &&
						(user.status === "Approved" ||
							user.status === "Disapproved" ||
							user.status === "Return") &&
						user.responded_by?.id !== user.id &&
						user.status_at
					)
			)
			.map((user) => ({
				approverName: approver.name,
				userName: user.name,
				status: user.status,
				status_at: user.status_at,
			}))
	);

	const [isPrintable, setIsPrintable] = useState(false);
	const handlePrintClick = () => {
		setIsPrintable(true);
		setTimeout(() => {
			window.print();
			setIsPrintable(false);
		}, 100);
	};

	const getUserAccessDetails = useCallback(async () => {
		try {
			const response = await userAccessService.getUserAccessDetails(id);
			const record =
				response.data && typeof response.data === "object"
					? response.data
					: response;

			if (record && record.id) {
				setData(record);
			} else {
				console.error("Fetched data is not a valid record:", record);
				notification.error({
					message: "Invalid Data",
					description: "The server returned an unexpected data format.",
				});
			}
		} catch (error) {
			console.error("Failed to fetch user access details:", error);
			notification.error({
				message: "Failed to Fetch Details",
				description: error.response?.data?.message || "An error occurred.",
			});
			navigate("/user-access-form");
		} finally {
			setLoading(false);
		}
	}, [id, navigate]);

	// Corrected useEffect structure
	useEffect(() => {
		if (data) {
			console.log("Approvers data:", data.approvers);
			console.log("UsersData:", data.usersData);
			console.log("Access Required:", data.access_required);
			console.log("Attachments:", data.attachments);
			console.log("Quality Assurances:", data.quality_assurances);
			console.log("Deployments:", data.deployments);
		}
		getUserAccessDetails();
	}, [getUserAccessDetails]);

	const handleSubmit = async (values, isParallel) => {
		setActionLoading(true);
		const formattedValues = {
			status,
			approved_disapproved: false,
			form_id: formId,
			key: parseInt(id),
			reason: values.message,
			...(isParallel && { assigned_user_id: assignedUserId }),

			...(specificReturnGroupId &&
				Number(specificReturnGroupId) > 0 && {
					approver_id: Number(specificReturnGroupId),
				}),
		};

		const serviceMethod = isParallel
			? approvalService.updateParallelStatus
			: approvalService.updateStatus;

		try {
			const response = await serviceMethod(formattedValues);
			notification.success({
				message: "Status Updated",
				description: response.message,
			});
			form.resetFields();
			await getUserAccessDetails();
			navigate("/dashboard");
		} catch (error) {
			notification.error({
				message: "Error",
				description: error.response?.data?.message || "An error occurred",
			});
		} finally {
			setActionLoading(false);
		}
	};

	// Format date function
	const formatDate = (dateString) => {
		if (!dateString) return "N/A";
		try {
			const date = new Date(dateString);
			if (isNaN(date.getTime())) return dateString;
			return `${String(date.getDate()).padStart(2, "0")}-${String(
				date.getMonth() + 1
			).padStart(2, "0")}-${date.getFullYear()}`;
		} catch (e) {
			return dateString;
		}
	};

	// New format date/time function
	const formatDateTime = (dateTimeString) => {
		if (!dateTimeString) return "N/A";
		try {
			const date = new Date(dateTimeString);
			if (isNaN(date.getTime())) return dateTimeString;
			const datePart = `${String(date.getDate()).padStart(2, "0")}-${String(
				date.getMonth() + 1
			).padStart(2, "0")}-${date.getFullYear()}`;
			const timePart = `${String(date.getHours()).padStart(2, "0")}:${String(
				date.getMinutes()
			).padStart(2, "0")}:${String(date.getSeconds()).padStart(2, "0")}`;
			return `${datePart} ${timePart}`;
		} catch (e) {
			return dateTimeString;
		}
	};

	if (loading) {
		return (
			<DefaultLayout>
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						height: "80vh",
					}}
				>
					<Spin size='large' />
				</div>
			</DefaultLayout>
		);
	}

	if (!data) {
		return (
			<DefaultLayout>
				<Header icon={<UserOutlined />} title='Error' />
				<Card>
					<p>
						Could not load the user access request details. It might have been
						deleted or an error occurred.
					</p>
					<Button type='primary' onClick={() => navigate("/user-access-form")}>
						Go Back
					</Button>
				</Card>
			</DefaultLayout>
		);
	}

	const Isadmin = currentUser?.roles?.includes("admin") || false;
	const canComment =
		data.status !== "Approved" && data.status !== "Return"
			? 1
			: data.comment_status === 1
			? 1
			: 0;

	const accessCreationDetailsPresent =
		data.access_creation_details &&
		(data.access_creation_details.email_details ||
			data.access_creation_details.user_id_details ||
			data.access_creation_details.access_rights_details ||
			data.windows_domain_login_id);

	const accessCount = data.access_required?.length || 0;
	const usersCount = data.usersData?.length || 0;

	// Access Required span
	const accessSpan = accessCount === 1 ? 8 : accessCount > 1 ? 24 : 12;

	// Users Impacted span
	const usersSpan = usersCount > 2 ? 24 : 16;
	return (
		<DefaultLayout>
			<Row gutter={[24, 24]}>
				<Col span={24}>
					<Header icon={<UserOutlined />} title='User Access Request Details' />
				</Col>
			</Row>
			<Spin spinning={actionLoading}>
				{data.quality_assurances && (
					<FeedbackModal
						title='QA Feedback'
						formId={formId}
						assurable_id={id}
					/>
				)}
				{data.deployments && (
					<DeploymentModal
						title='Deployments'
						data_deployment={data.deployments}
					/>
				)}
				{data.assigned_task && (
					<TaskModal
						title='Tasks Logs'
						data_tasks={data.assigned_task.assign_task_teams}
					/>
				)}

				{/* PRINTABLE CARD (Optimized for single-page layout) */}
				{isPrintable && (
					<Card
						style={{
							minHeight: "100vh",
							border: "none",
							padding: "0 20px",
							background: "#fff",
						}}
						id='ppp'
					>
						{/* HEADER */}
						<Row gutter={[24, 24]} align='middle'>
							<Col span={6}>
								<img
									src={
										currentUser?.company?.logo
											? currentUser.company.logo.replace("/uploads", "")
											: "https://www.lucky-cement.com/wp-content/uploads/2017/02/lucky-logo.png"
									}
									alt='Logo'
									style={{ height: "50px", width: "auto" }}
								/>
							</Col>
							<Col span={12} style={{ textAlign: "center" }}>
								<h2 style={{ marginBottom: 0, fontSize: "22px" }}>
									<strong>USER ACCESS FORM</strong>
								</h2>
								<p style={{ margin: 0, fontSize: "14px", color: "#666" }}>
									LUCKY CEMENT LIMITED
								</p>
							</Col>
							<Col span={6} style={{ textAlign: "right" }}>
								<Tag
									color='blue'
									style={{ fontSize: "12px", fontWeight: "bold" }}
								>
									Internal Document
								</Tag>
							</Col>
						</Row>
						<Divider style={{ margin: "12px 0" }} />
						<Row gutter={[24, 12]}>
							<Col span={8}>
								<p style={{ margin: 0, fontSize: "11px", color: "#888" }}>
									Sequence No
								</p>
								<p style={{ margin: 0, fontWeight: "bold" }}>
									{data.sequence_no}
								</p>
							</Col>
							<Col span={8} style={{ textAlign: "center" }}>
								<p style={{ margin: 0, fontSize: "11px", color: "#888" }}>
									Request Status
								</p>
								<Tag
									color={
										data.status === "Approved"
											? "green"
											: data.status === "Pending"
											? "orange"
											: "red"
									}
									style={{ margin: 0 }}
								>
									{data.status}
								</Tag>
							</Col>
							<Col span={8} style={{ textAlign: "right" }}>
								<p style={{ margin: 0, fontSize: "11px", color: "#888" }}>
									Printed Date
								</p>
								<p style={{ margin: 0 }}>
									{new Date().toLocaleDateString("en-GB", {
										day: "2-digit",
										month: "short",
										year: "numeric",
									})}
								</p>
							</Col>
							<Col span={24}>
								<p
									style={{
										margin: "8px 0 0 0",
										fontSize: "11px",
										color: "#888",
									}}
								>
									Request Title
								</p>
								<p style={{ margin: 0, fontWeight: "bold", fontSize: "16px" }}>
									{data.request_title}
								</p>
							</Col>
						</Row>

						<Divider style={{ margin: "12px 0" }} />
						<h3
							style={{
								fontSize: "14px",
								borderBottom: "1px solid #eee",
								paddingBottom: "4px",
							}}
						>
							Request & User Information
						</h3>
						<Row gutter={[16, 12]}>
							<Col span={6}>
								<p style={{ margin: 0, fontSize: "11px", color: "#888" }}>
									Requested By
								</p>
								<p style={{ margin: "4px 0 0 0", fontWeight: "500" }}>
									{data.created_by?.name || "N/A"}
								</p>
							</Col>
							<Col span={6}>
								<p style={{ margin: 0, fontSize: "11px", color: "#888" }}>
									Access Type
								</p>
								<p style={{ margin: "4px 0 0 0", fontWeight: "500" }}>
									{data.access_type || "N/A"}
								</p>
							</Col>
							<Col span={6}>
								<p style={{ margin: 0, fontSize: "11px", color: "#888" }}>
									Start Date
								</p>
								<p style={{ margin: "4px 0 0 0", fontWeight: "500" }}>
									{formatDate(data.start_date)}
								</p>
							</Col>
							<Col span={6}>
								<p style={{ margin: 0, fontSize: "11px", color: "#888" }}>
									End Date
								</p>
								<p style={{ margin: "4px 0 0 0", fontWeight: "500" }}>
									{formatDate(data.end_date)}
								</p>
							</Col>
							<Col span={6}>
								<p
									style={{
										margin: "8px 0 0 0",
										fontSize: "11px",
										color: "#888",
									}}
								>
									Department
								</p>
								<p style={{ margin: "4px 0 0 0", fontWeight: "500" }}>
									{data.department?.name || "N/A"}
								</p>
							</Col>
							<Col span={6}>
								<p
									style={{
										margin: "8px 0 0 0",
										fontSize: "11px",
										color: "#888",
									}}
								>
									Location
								</p>
								<p style={{ margin: "4px 0 0 0", fontWeight: "500" }}>
									{data.location?.name || "N/A"}
								</p>
							</Col>
							<Col span={6}>
								<p
									style={{
										margin: "8px 0 0 0",
										fontSize: "11px",
										color: "#888",
									}}
								>
									Access Required
								</p>
								<div style={{ marginTop: "4px" }}>
									{data.access_required?.map((item, i) => (
										<Tag key={i} style={{ margin: "2px" }}>
											{item}
										</Tag>
									))}
								</div>
							</Col>
							<Col span={6}>
								<p
									style={{
										margin: "8px 0 0 0",
										fontSize: "11px",
										color: "#888",
									}}
								>
									Access Rights Required
								</p>
								<div style={{ marginTop: "4px" }}>
									{data.access_rights_required?.map((item, i) => (
										<Tag key={i} style={{ margin: "2px" }}>
											{item}
										</Tag>
									))}
								</div>
							</Col>
							<Col span={6}>
								<p
									style={{
										margin: "8px 0 0 0",
										fontSize: "11px",
										color: "#888",
									}}
								>
									Users Impacted
								</p>
								<div style={{ marginTop: "4px" }}>
									{data.usersData?.map((u) => (
										<Tag key={u.id} style={{ margin: "2px" }}>
											{u.name}
										</Tag>
									))}
								</div>
							</Col>
						</Row>

						<Divider style={{ margin: "12px 0" }} />
						<Row gutter={[16, 12]}>
							<Col span={12}>
								<p style={{ margin: 0, fontSize: "11px", color: "#888" }}>
									Usage & Remarks
								</p>
								<div
									style={{ marginTop: 4, fontSize: "13px", lineHeight: "1.5" }}
									dangerouslySetInnerHTML={{
										__html: replaceLineBreaks(data.usage_remarks || "N/A"),
									}}
								/>
							</Col>
							<Col span={12}>
								<p style={{ margin: 0, fontSize: "11px", color: "#888" }}>
									Business Requirements
								</p>
								<div
									style={{ marginTop: 4, fontSize: "13px", lineHeight: "1.5" }}
									dangerouslySetInnerHTML={{
										__html: replaceLineBreaks(
											data.business_requirements || "N/A"
										),
									}}
								/>
							</Col>
						</Row>

						<Divider style={{ margin: "12px 0" }} />
						<h3
							style={{
								fontSize: "14px",
								borderBottom: "1px solid #eee",
								paddingBottom: "4px",
							}}
						>
							ITS Processing Info
						</h3>
						<Row gutter={[16, 12]}>
							<Col span={8}>
								<p style={{ margin: 0, fontSize: "11px", color: "#888" }}>
									Request Received On
								</p>
								<p style={{ margin: "4px 0 0 0", fontWeight: "500" }}>
									{formatDateTime(data.created_at)}
								</p>
							</Col>
							<Col span={8}>
								<p style={{ margin: 0, fontSize: "11px", color: "#888" }}>
									Access Provided On
								</p>
								<p style={{ margin: "4px 0 0 0", fontWeight: "500" }}>
									{formatDateTime(data.access_provided_on)}
								</p>
							</Col>
							<Col span={8}>
								<p style={{ margin: 0, fontSize: "11px", color: "#888" }}>
									Windows Domain ID
								</p>
								<Tag
									color={data.windows_domain_login_id ? "green" : "red"}
									style={{ marginTop: "4px" }}
								>
									{data.windows_domain_login_id ? "Created" : "No"}
								</Tag>
							</Col>
						</Row>

						<div style={{ margin: "20px 0" }}>
							<Divider
								orientation='center'
								dashed
								style={{ fontSize: "12pt", fontWeight: "bold" }}
							>
								TO BE FILLED BY ITS DEPARTMENT
							</Divider>
						</div>

						{accessCreationDetailsPresent && (
							<div style={{ pageBreakInside: "avoid" }}>
								<h4 style={{ fontSize: "12px", marginBottom: "8px" }}>
									Access Creation Actions
								</h4>
								<Row gutter={[16, 12]}>
									<Col span={8}>
										<p style={{ margin: 0, fontSize: "11px", color: "#888" }}>
											Access Rights Details
										</p>
										<div
											style={{ marginTop: 4, fontSize: "12px" }}
											dangerouslySetInnerHTML={{
												__html: replaceLineBreaks(
													data.access_creation_details.access_rights_details
												),
											}}
										/>
									</Col>
									{data.access_creation_details?.user_id_details && (
										<Col span={8}>
											<p style={{ margin: 0, fontSize: "11px", color: "#888" }}>
												User ID Details
											</p>
											<div
												style={{ marginTop: 4, fontSize: "12px" }}
												dangerouslySetInnerHTML={{
													__html: replaceLineBreaks(
														data.access_creation_details.user_id_details
													),
												}}
											/>
										</Col>
									)}
									{data.access_creation_details?.email_details && (
										<Col span={8}>
											<p style={{ margin: 0, fontSize: "11px", color: "#888" }}>
												Email Details
											</p>
											<div
												style={{ marginTop: 4, fontSize: "12px" }}
												dangerouslySetInnerHTML={{
													__html: replaceLineBreaks(
														data.access_creation_details.email_details
													),
												}}
											/>
										</Col>
									)}
								</Row>
							</div>
						)}

						<Divider style={{ margin: "12px 0" }} />
						<div style={{ pageBreakInside: "avoid" }}>
							<p style={{ margin: 0, fontSize: "11px", color: "#888" }}>
								ITS Remarks
							</p>
							<div
								style={{ marginTop: 4, fontSize: "12px" }}
								dangerouslySetInnerHTML={{
									__html: replaceLineBreaks(data.its_remarks || "N/A"),
								}}
							/>
						</div>

						<div style={{ pageBreakInside: "avoid", marginTop: "20px" }}>
							<h3
								style={{
									fontSize: "14px",
									borderBottom: "1px solid #eee",
									paddingBottom: "4px",
									textAlign: "center",
								}}
							>
								Approvers
							</h3>
							<table
								style={{
									width: "100%",
									borderCollapse: "collapse",
									fontSize: "11px",
									marginTop: "8px",
								}}
							>
								<thead>
									<tr style={{ background: "#f5f5f5" }}>
										<th style={{ padding: "6px", border: "1px solid #eee" }}>
											Approver Name
										</th>
										<th style={{ padding: "6px", border: "1px solid #eee" }}>
											Status
										</th>
										<th style={{ padding: "6px", border: "1px solid #eee" }}>
											Action Performed at
										</th>
									</tr>
								</thead>
								<tbody>
									{tableData && tableData.length > 0 ? (
										tableData.map((row, index) => (
											<tr key={index}>
												<td
													style={{ padding: "6px", border: "1px solid #eee" }}
												>
													{row.userName}
												</td>
												<td
													style={{ padding: "6px", border: "1px solid #eee" }}
												>
													<span
														style={{
															color:
																getStatusColor(row.status) === "green"
																	? "green"
																	: getStatusColor(row.status) === "yellow"
																	? "#DAA520"
																	: "black",
														}}
													>
														{row.status}
													</span>
												</td>
												<td
													style={{ padding: "6px", border: "1px solid #eee" }}
												>
													{row.status_at}
												</td>
											</tr>
										))
									) : (
										<tr>
											<td
												colSpan='3'
												style={{ padding: "6px", textAlign: "center" }}
											>
												No approver data available
											</td>
										</tr>
									)}
								</tbody>
							</table>
						</div>

						{data && data.attachments && data.attachments.length > 0 && (
							<div style={{ pageBreakInside: "avoid", marginTop: "12px" }}>
								<h3
									style={{
										fontSize: "14px",
										borderBottom: "1px solid #eee",
										paddingBottom: "4px",
									}}
								>
									Attachments
								</h3>
								<table
									style={{
										width: "100%",
										borderCollapse: "collapse",
										fontSize: "11px",
										marginTop: "8px",
									}}
								>
									<thead>
										<tr>
											<th
												style={{
													textAlign: "left",
													padding: "6px",
													borderBottom: "1px solid #eee",
												}}
											>
												Title
											</th>
											<th
												style={{
													textAlign: "left",
													padding: "6px",
													borderBottom: "1px solid #eee",
												}}
											>
												Preview
											</th>
										</tr>
									</thead>
									<tbody>
										{data.attachments.map((attachment) => {
											const isImage = (filename) =>
												/\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(filename);
											const fileUrl = attachment.filename;
											const fileTitle =
												attachment.original_title || attachment.filename;
											return (
												<tr key={attachment.id}>
													<td
														style={{
															padding: "6px",
															borderBottom: "1px solid #eee",
														}}
													>
														{fileTitle}
													</td>
													<td
														style={{
															padding: "6px",
															borderBottom: "1px solid #eee",
														}}
													>
														{isImage(fileUrl) ? (
															<img
																src={fileUrl}
																alt={fileTitle}
																style={{
																	width: 40,
																	height: 40,
																	objectFit: "cover",
																	borderRadius: 4,
																	border: "1px solid #eee",
																}}
															/>
														) : (
															<span style={{ color: "#aaa", fontSize: 10 }}>
																No preview
															</span>
														)}
													</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</div>
						)}

						<Divider style={{ margin: "20px 0 10px 0" }} />
						<Row gutter={[16, 16]}>
							<Col
								span={24}
								style={{ fontSize: "10px", color: "#888", textAlign: "center" }}
							>
								<p style={{ margin: 0 }}>
									This is a computer-generated document. No signature is
									required.
								</p>
								<p style={{ margin: "4px 0" }}>
									Detailed information for request{" "}
									<strong>{data.sequence_no}</strong> can be accessed at:
									<br />
									<span style={{ color: "#1890ff" }}>
										{window.location.href}
									</span>
								</p>
							</Col>
						</Row>
					</Card>
				)}

				{/* WEB PAGE CARD - Retains Collapsible Sections */}
				<Card style={{ minHeight: "100vh" }} id='qqq'>
					<Row gutter={[24, 24]}>
						<Col lg={5} md={5} sm={7} xs={7}>
							<CreatedByComponent data={data} />
							<Button
								type='primary'
								onClick={handlePrintClick}
								style={{
									marginTop: "20px",
									marginBottom: "20px",
									width: "100%",
								}}
							>
								Print Document
							</Button>
							{(data.status === "Pending" || data.status === "Approved") &&
								hasEditPermission && (
									<AddApproversComponent data={data} formId={formId} />
								)}
							<ApproversComponent
								approvers={Array.isArray(data.approvers) ? data.approvers : []}
							/>
						</Col>

						<Col lg={19} md={19} sm={17} xs={17}>
							{data?.approved_disapproved && (
								<div
									style={{
										position: "static",
										top: 1,
										zIndex: 99,
										background: "#fff",
										padding: "10px",
										border: "1px solid #f0f0f0",
										marginBottom: "10px",
										borderRadius: "8px",
									}}
								>
									<b>Approval Request</b>
									<ApproveDisapproveComponent
										form={form}
										handleStatus={(values) => handleSubmit(values, false)}
										loading={actionLoading}
										setStatus={setStatus}
										dataRecord={data}
										setSpecificReturnGroupId={setSpecificReturnGroupId}
									/>
								</div>
							)}
							{data.parallel_approved_disapproved && (
								<div
									style={{
										position: "static",
										top: data.approved_disapproved ? "150px" : "1px",
										zIndex: 99,
										background: "#fff",
										padding: "10px",
										border: "1px solid #f0f0f0",
										marginBottom: "10px",
										borderRadius: "8px",
									}}
								>
									<b>Parallel Approval Request</b>
									<ApproveDisapproveComponent
										form={form}
										handleStatus={(values) => handleSubmit(values, true)}
										loading={actionLoading}
										setStatus={setStatus}
										parallel={true}
										parallelUsers={
											data.parallel_approved_disapproved_users || []
										}
										setAssignedUserId={setAssignedUserId}
										reloadData={getUserAccessDetails}
										dataRecord={data}
										setSpecificReturnGroupId={setSpecificReturnGroupId}
									/>
								</div>
							)}
							{data.assigned_task_to_me && (
								<div
									style={{
										position: "sticky",
										top: "1px",
										zIndex: 99,
										background: "#fff",
										padding: "10px",
										border: "1px solid #f0f0f0",
										marginBottom: "10px",
										borderRadius: "8px",
									}}
								>
									<TaskStatus
										data={data}
										reloadData={getUserAccessDetails}
										formId={formId}
									/>
								</div>
							)}

							<Card title='Basic Information' className='mb-10'>
								<Row gutter={[16, 16]}>
									<Col xs={24} sm={12} lg={8}>
										<p className='fs-12 mb-10 bold'>Sequence No</p>
										<Tag>{data.sequence_no}</Tag>
									</Col>
									<Col xs={24} sm={12} lg={8}>
										<p className='fs-12 mb-10 bold'>Request Title</p>
										<Tag>{data.request_title}</Tag>
									</Col>
									<Col xs={24} sm={12} lg={8}>
										<p className='fs-12 mb-10 bold'>Status</p>
										<Tag
											color={
												data.status === "Pending"
													? "orange"
													: data.status === "Approved"
													? "green"
													: data.status === "Return"
													? "red"
													: "default"
											}
										>
											{data.status}
										</Tag>
									</Col>
									<Col xs={24} sm={12} lg={8}>
										<p className='fs-12 mb-10 bold'>Access Type</p>
										<Tag autoCapitalize='true'>{data.access_type}</Tag>
									</Col>
									<Col xs={24} sm={12} lg={8}>
										<p className='fs-12 mb-10 bold'>Department</p>
										<Tag>{data.department?.name || "N/A"}</Tag>
									</Col>
									<Col xs={24} sm={12} lg={8}>
										<p className='fs-12 mb-10 bold'>Location</p>
										<Tag>{data.location?.name || "N/A"}</Tag>
									</Col>
									<Col xs={24} sm={12} lg={8}>
										<p className='fs-12 mb-10 bold'>Other</p>
										<Tag>{data.other || "N/A"}</Tag>
									</Col>
									<Col xs={24} sm={12} lg={8}>
										<p className='fs-12 mb-10 bold'>Start Date</p>
										<Tag color='green'>{formatDate(data.start_date)}</Tag>
									</Col>
									<Col xs={24} sm={12} lg={8}>
										<p className='fs-12 mb-10 bold'>End Date</p>
										<Tag color='green'>{formatDate(data.end_date)}</Tag>
									</Col>
								</Row>
							</Card>
							<Card title='Access Request' className='mb-10'>
								<Row gutter={[16, 16]}>
									{data.access_type && (
										<Col xs={8}>
											<p className='fs-12 mb-10 bold'>Access Type</p>
											<Tag>{data.access_type}</Tag>
										</Col>
									)}
									{data.access_required && (
										<Col xs={8}>
											<p className='fs-12 mb-10 bold'>Access Required</p>
											<div>
												{data.access_required.map(
													(
														accessItem,
														index // Rename to avoid confusion
													) => (
														<Tag key={`access-${index}`} className='mb-8'>
															{accessItem}
														</Tag>
													)
												)}
											</div>
										</Col>
									)}
									{data.access_rights_required && (
										<Col xs={8}>
											<p className='fs-12 mb-10 bold'>Access Rights Required</p>
											<div>
												{data.access_rights_required.map(
													(
														accessRightsItem,
														index // Rename to avoid confusion
													) => (
														<Tag key={`access-rights-${index}`} className='mb-8'>
															{accessRightsItem}
														</Tag>
													)
												)}
											</div>
										</Col>
									)}
									{data.usersData && Array.isArray(data.usersData) && (
										<Col xs={8}>
											<p className='fs-12 mb-10 bold'>For Users</p>
											<div>
												{data.usersData.map((user) => (
													<Tag
														key={user.id}
														title={user.email}
														className='mb-8'
													>
														{user.name}
													</Tag>
												))}
											</div>
										</Col>
									)}
								</Row>
							</Card>
							<Row gutter={[16, 16]}>
								<Col key='usage-remarks' xs={24} sm={24} md={12}>
									<Card title='Usage & Remarks'>
										<div
											dangerouslySetInnerHTML={{
												__html: replaceLineBreaks(
													data.usage_remarks || "No remarks provided."
												),
											}}
										/>
									</Card>
								</Col>
								<Col key='business-requirements' xs={24} sm={24} md={12}>
									<Card title='Business Requirements'>
										<div
											dangerouslySetInnerHTML={{
												__html: replaceLineBreaks(
													data.business_requirements ||
														"No business requirements provided."
												),
											}}
										/>
									</Card>
								</Col>
							</Row>

							{/* Attachments Section */}
							{data.attachments && data.attachments.length > 0 && (
								<Card title='Attachments' className='mb-10'>
									<Row gutter={[16, 16]}>
										{data.attachments.map((attachment, index) => (
											<Col xs={24} sm={12} key={index}>
												<div className='attachment-item'>
													<p className='fs-12 mb-5 bold'>
														Attachment {index + 1}
													</p>
													<Tag color='green'>
														{attachment.original_title || `File ${index + 1}`}
													</Tag>
												</div>
											</Col>
										))}
									</Row>
								</Card>
							)}

							{/* COLLAPSIBLE ITS REMARKS */}
							{data.its_remarks && (
								<Card className='mb-10'>
									<div
										style={{ margin: "20px" }}
										className='its-department-divider-wrapper'
									>
										<Divider
											orientation='center'
											dashed
											style={{ fontSize: "14pt", fontWeight: "bold" }}
										>
											TO BE FILLED BY ITS DEPARTMENT
										</Divider>
									</div>
									<Row gutter={[16, 16]}>
										<Col span={24}>
											<p className='fs-12 mb-10 bold'>ITS Department Remarks</p>
											<div
												dangerouslySetInnerHTML={{
													__html: replaceLineBreaks(data.its_remarks),
												}}
											/>
										</Col>
									</Row>
								</Card>
							)}

							{/* NEW SECTION: Access Provided On (Web - Non-collapsible as it's a single key metric) */}
							{data.access_provided_on && (
								<Card className='mb-10'>
									<Row gutter={[16, 16]}>
										<Col xs={24} sm={12}>
											<p className='fs-12 mb-10 bold'>Access Provided On</p>
											<Tag color='blue'>
												{formatDateTime(data.access_provided_on)}
											</Tag>
										</Col>

										{data.access_creation_details.user_id_details && (
											<Col xs={24} sm={12}>
												<p className='fs-12 mb-10 bold'>
													Windows Domain Login ID Created
												</p>
												<Tag
													color={data.windows_domain_login_id ? "green" : "red"}
												>
													Yes
												</Tag>
											</Col>
										)}
									</Row>
								</Card>
							)}

							{/* COLLAPSIBLE ACCESS CREATION ACTIONS */}
							{accessCreationDetailsPresent && (
								<Collapse
									defaultActiveKey={["access_actions"]}
									className='mb-10'
								>
									<Card header='Access Creation Actions (ITS Completion Details)'>
										<Row gutter={[16, 16]}>
											{data.access_creation_details?.user_id_details && (
												<Col xs={24} sm={12} md={8}>
													<p className='fs-12 mb-10 bold'>
														User ID Details (Filled by ITS)
													</p>
													<div
														dangerouslySetInnerHTML={{
															__html: replaceLineBreaks(
																data.access_creation_details.user_id_details
															),
														}}
													/>
												</Col>
											)}
											{data.access_creation_details?.access_rights_details && (
												<Col xs={24} sm={12} md={8}>
													<p className='fs-12 mb-10 bold'>
														Access Rights Details (Filled by ITS)
													</p>
													<div
														dangerouslySetInnerHTML={{
															__html: replaceLineBreaks(
																data.access_creation_details
																	.access_rights_details
															),
														}}
													/>
												</Col>
											)}
											{data.access_creation_details?.email_details && (
												<Col xs={24} sm={12} md={8}>
													<p className='fs-12 mb-10 bold'>
														Email Details (Filled by ITS)
													</p>
													<div
														dangerouslySetInnerHTML={{
															__html: replaceLineBreaks(
																data.access_creation_details.email_details
															),
														}}
													/>
												</Col>
											)}
										</Row>
									</Card>
								</Collapse>
							)}

							<CommentSystem
								id={id}
								form_id={formId}
								status={data.status}
								comment_status={canComment}
							/>
						</Col>
					</Row>
				</Card>
			</Spin>
		</DefaultLayout>
	);
};

export default UserAccessDetails;
